import React, { useRef, useState, useEffect } from 'react';
import { set, get, map, isEmpty, omit } from 'lodash/fp';
import {
  Form,
  Schema,
  FormControl,
  ControlLabel,
  CheckPicker,
  Button,
  ButtonToolbar,
  Icon,
  Tooltip,
  Whisper,
  DateRangePicker,
  Panel,
  Radio,
  RadioGroup,
  FormGroup
} from 'rsuite';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchInventoryLocationOptions,
  fetchDonorSummaryReport
} from '../../../../redux/Inventory/sagas/routines';
import { getIsFetching } from '../../../../redux/Inventory/selectors/reports/donor-summary';
import {
  getLocationOptions,
  getIsFetchingLocationOptions
} from '../../../../redux/Inventory/selectors/components/adjust-item-form';
import {
  resetDonorSummaryReport,
  updateDonorSummaryReportSearchQuery
} from '../../../../redux/Inventory/actions/reports/donor-summary';
import {
  InlineHelpBlock,
  InlineFormGroup
} from '../../../widgets/FormElements';
import { DateRangeOptions, GroupByOptions } from './form-options';
import ReportTemplatesModal from '../../../../containers/ReportTemplatesModal';
import { DONOR_SUMMARY } from '../../../../helpers/ReportTemplateTypes';
import { toStandardDate } from '../../../../helpers/dates';

const StyledPanel = styled(Panel)`
  > .rs-panel-body {
    padding-bottom: 8px;
  }
`;

const StyledFormGroup = styled(InlineFormGroup)`
  margin-bottom: 0 !important;
`;

const { StringType, ArrayType } = Schema.Types;

const PERFORMED_ON = 'performedOn';
const CREATED_AT = 'createdAt';

export default () => {
  const formRef = useRef();
  const dispatch = useDispatch();
  const [formValue, setFormValue] = useState({});
  const [formError, setFormError] = useState({});
  const [dateRangeField, setDateRangeField] = useState(CREATED_AT);
  const [showPresetModal, setShowPresetModal] = useState(false);

  const isFetching = useSelector(getIsFetching);
  const locationOptions = useSelector(getLocationOptions);
  const isLoadingLocations = useSelector(getIsFetchingLocationOptions);

  const revalidateForm = () => {
    setTimeout(formRef.current.check, 1);
  };

  const setDefaultFormValue = () => {
    const dateRange = get('0.value', DateRangeOptions);
    const inventoryLocationId = map('value', locationOptions);
    setFormValue({ [dateRangeField]: dateRange, inventoryLocationId });
  };

  useEffect(() => {
    dispatch(fetchInventoryLocationOptions());
  }, []);

  useEffect(
    () => {
      if (!isEmpty(locationOptions)) {
        setDefaultFormValue();
      }
    },
    [locationOptions]
  );

  const executeSearch = query => {
    const dateRange = map(toStandardDate, query[dateRangeField]);
    const searchQuery = set(dateRangeField, dateRange, query);
    dispatch(fetchDonorSummaryReport(searchQuery));
    dispatch(updateDonorSummaryReportSearchQuery(searchQuery));
  };

  const handleFormChange = value => {
    setFormValue(value);
  };

  const handleFormCheck = error => {
    setFormError(error);
  };

  const handleFormSubmit = () => {
    const noError = formRef.current.check();
    if (noError) {
      executeSearch(formValue);
    }
  };

  const handleFormReset = () => {
    setDefaultFormValue();
    revalidateForm();
    dispatch(resetDonorSummaryReport());
  };

  const onHidePresetModal = () => {
    setShowPresetModal(false);
  };

  const handleTogglePresetModal = () => {
    const showModal = !showPresetModal;
    setShowPresetModal(showModal);
  };

  const handleSelectTemplate = ({ file }) => {
    onHidePresetModal();
    const { dateRange } = get(dateRangeField, formValue);
    const searchQuery = { ...file, [dateRangeField]: dateRange };
    setFormValue(searchQuery);
    revalidateForm();

    if (!isEmpty(dateRange)) {
      executeSearch(searchQuery);
    }
  };

  const handleChangeDateRangeOption = selectedField => {
    const dateRangeValue = get(dateRangeField, formValue);
    setFormValue({
      ...omit(dateRangeField, formValue),
      [selectedField]: dateRangeValue
    });
    setDateRangeField(selectedField);
  };

  const FormModel = Schema.Model({
    [dateRangeField]: ArrayType().isRequired('This field is required'),
    inventoryLocationId: ArrayType()
      .isRequired('This field is required')
      .minLength(1, 'Please select at least 1.'),
    groupBy: ArrayType().of(StringType())
  });

  const disabledAction = isFetching || isLoadingLocations;

  return (
    <StyledPanel bordered>
      <Form
        model={FormModel}
        ref={formRef}
        formValue={formValue}
        onChange={handleFormChange}
        onCheck={handleFormCheck}
      >
        <StyledFormGroup>
          <ControlLabel>
            Select Date From - To*
            <InlineHelpBlock tooltip>
              Select the date range you are reporting for and click
              &quot;OK&quot;
            </InlineHelpBlock>
          </ControlLabel>
          <FormControl
            style={{ width: 230 }}
            placeholder="Select date range"
            name={dateRangeField}
            accepter={DateRangePicker}
            ranges={DateRangeOptions}
            errorMessage={get(dateRangeField, formError)}
          />
          <FormGroup>
            <RadioGroup
              inline
              name="dateRange"
              defaultValue={dateRangeField}
              onChange={handleChangeDateRangeOption}
            >
              <Radio value={CREATED_AT}>Log Date</Radio>
              <Radio value={PERFORMED_ON}>Performed Date</Radio>
            </RadioGroup>
          </FormGroup>
        </StyledFormGroup>
        <StyledFormGroup>
          <ControlLabel>
            Inventory Locations*
            <InlineHelpBlock tooltip>
              Select one or multiple locations.
            </InlineHelpBlock>
          </ControlLabel>
          <FormControl
            style={{ width: 180 }}
            placeholder={
              isLoadingLocations ? 'Loading...' : 'Inventory Locations'
            }
            name="inventoryLocationId"
            accepter={CheckPicker}
            data={locationOptions}
            errorMessage={formError.inventoryLocationId}
          />
        </StyledFormGroup>
        <StyledFormGroup>
          <ControlLabel>
            Columns
            <InlineHelpBlock tooltip>
              Select the type of Columns you want to run.
            </InlineHelpBlock>
          </ControlLabel>
          <FormControl
            style={{ width: 170 }}
            placeholder="Columns"
            name="groupBy"
            accepter={CheckPicker}
            data={GroupByOptions}
            errorMessage={formError.groupBy}
          />
        </StyledFormGroup>
        <ButtonToolbar style={{ float: 'right', marginTop: 24 }}>
          <Button
            appearance="primary"
            onClick={handleFormSubmit}
            disabled={disabledAction}
          >
            <Icon icon="search" /> Search
          </Button>
          <Button
            appearance="default"
            onClick={handleFormReset}
            disabled={disabledAction}
          >
            <Icon icon="refresh" /> Reset
          </Button>
          <Whisper
            placement="top"
            trigger="hover"
            speaker={<Tooltip>View Presets</Tooltip>}
          >
            <Button
              appearance="primary"
              color="green"
              onClick={handleTogglePresetModal}
              disabled={disabledAction}
            >
              <Icon icon="list" /> Presets
            </Button>
          </Whisper>
        </ButtonToolbar>
      </Form>
      <ReportTemplatesModal
        show={showPresetModal}
        onHide={onHidePresetModal}
        onSelect={handleSelectTemplate}
        fileType={DONOR_SUMMARY}
      />
    </StyledPanel>
  );
};
